import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  CalendarX2, Repeat, Users2, Clock4, Layers, Bot, Smartphone,
  CheckCircle2, ShieldCheck, MapPin,
} from "lucide-react";
import AnimatedHero from "../components/AnimatedHero";
import Reveal from "../components/Reveal";
import ChatConversation from "../components/ChatConversation";
import ChatWidget from "../components/ChatWidget";
import StickyActions from "../components/StickyActions";
import AssistantLauncher from "../components/AssistantLauncher";
import CookieBanner from "../components/CookieBanner";
import SocialProof from "../components/SocialProof";
import PricingSection from "../components/PricingSection";
import AiSalesAgentDemo from "../components/AiSalesAgentDemo";
import ContactActions from "../components/ContactActions";
import CallbackForm from "../components/CallbackForm";
import { useChat } from "../context/ChatContext";

const PROBLEMS = [
  { icon: CalendarX2, t: "Im Sommer volle Auftragsbücher, im Winter Funkstille." },
  { icon: Repeat, t: "Abhängig von Empfehlungen — aber nicht planbar." },
  { icon: Users2, t: "Teure, geteilte Leads von Portalen, die 5 Betriebe gleichzeitig bekommen." },
  { icon: Clock4, t: "Keine Zeit, sich auch noch um Marketing zu kümmern." },
];

const STEPS = [
  { icon: Layers, t: "KI-Funnel", d: "Eine Seite, die aus dem Klick echte Anfragen macht — psychologisch aufgebaut, geringe Absprungrate." },
  { icon: Bot, t: "BA Media Assistent (24/7)", d: "Qualifiziert jede Anfrage automatisch, Tag und Nacht, in natürlicher Sprache." },
  { icon: Smartphone, t: "Qualifizierte Anfragen aufs Handy", d: "Sie sprechen nur noch mit Leuten, die wirklich kaufen wollen." },
];

const WHY_IMG = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50fGVufDB8fHx8MTc4MjQzNzUyN3ww&ixlib=rb-4.1.0&q=85";

function SectionLabel({ children }) {
  return <p className="eyebrow mb-3">{children}</p>;
}

export default function Landing() {
  const { openChat } = useChat();
  const solutionRef = useRef(null);

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#0E0F12] pb-20 md:pb-0">
      {/* Nav */}
      <nav className="fixed top-0 z-30 w-full border-b border-white/5 bg-[#0E0F12]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_handwerk-sales-agent/artifacts/77xmowg7_BAMP%202026.png"
              alt="BA Media Performance"
              className="h-16 w-auto md:h-20"
            />
          </Link>
          <button
            data-testid="nav-cta"
            onClick={openChat}
            className="rounded-md bg-[#3DDC97] px-4 py-2 text-sm font-semibold text-[#0E0F12] transition-colors hover:bg-[#2BBF7E]"
          >
            Kurz prüfen
          </button>
        </div>
      </nav>

      <AnimatedHero onStartChat={openChat} onHowItWorks={() => scrollTo(solutionRef)} />

      {/* Beweis ganz oben */}
      <SocialProof />

      {/* Problem */}
      <section className="border-b border-white/5 px-6 py-14 md:py-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <SectionLabel>Kennen Sie das?</SectionLabel>
            <h2 className="font-display max-w-2xl text-3xl font-bold text-white md:text-4xl">
              Das Problem ist nicht Ihre Arbeit.
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PROBLEMS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08}>
                <div className="group h-full rounded-lg border border-white/5 bg-[#14161B] p-6 transition-colors hover:border-[#3DDC97]/30">
                  <p.icon className="mb-4 text-[#3DDC97]" size={26} />
                  <p className="text-[15px] leading-relaxed text-gray-300">{p.t}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg text-gray-400">
              Es ist das <span className="font-semibold text-white">fehlende System</span> dahinter.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Lösung */}
      <section ref={solutionRef} className="border-b border-white/5 px-6 py-14 md:py-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <SectionLabel>So funktioniert unser System</SectionLabel>
            <h2 className="font-display max-w-2xl text-3xl font-bold text-white md:text-4xl">
              In drei Schritten zu planbaren Anfragen.
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.12}>
                <div className="relative h-full rounded-lg border border-white/5 bg-[#14161B] p-8">
                  <span className="font-display absolute right-6 top-6 text-5xl font-extrabold text-white/5">
                    0{i + 1}
                  </span>
                  <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-md bg-[#3DDC97]/10 text-[#3DDC97]">
                    <s.icon size={24} />
                  </div>
                  <h3 className="font-display text-xl font-bold text-white">{s.t}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-gray-400">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14">
              <h3 className="font-display text-2xl font-bold text-white md:text-3xl">
                So arbeitet Ihr digitaler Mitarbeiter – rund um die Uhr.
              </h3>
              <div className="mt-8">
                <AiSalesAgentDemo />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="border-b border-white/5 px-6 py-14 md:py-16">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <SectionLabel>Warum wir</SectionLabel>
            <h2 className="font-display text-3xl font-bold text-white md:text-4xl">
              Nicht noch eine Agentur.
            </h2>
            <ul className="mt-6 space-y-4">
              {[
                "Praktiker statt Theoretiker — vom Unternehmer für Unternehmer.",
                "Eigenes System, kein zugekauftes Tool.",
                "Kein Knebelvertrag, faire und ehrliche Zusammenarbeit.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-gray-300">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-[#3DDC97]" size={20} />
                  <span className="text-[15px] leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 inline-flex items-center gap-3 rounded-lg border border-[#3DDC97]/30 bg-[#3DDC97]/10 px-5 py-4">
              <MapPin className="shrink-0 text-[#3DDC97]" size={22} />
              <p className="text-sm font-medium text-white">
                Exklusivität: <span className="text-[#3DDC97]">In der Regel ein Betrieb pro Dienstleistung und Region.</span>
                <br />
                <span className="text-gray-400">Ob Ihr Gebiet verfügbar ist, klären wir ehrlich im Erstgespräch.</span>
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden rounded-lg border border-white/10">
              <img src={WHY_IMG} alt="Blueprint" className="h-[360px] w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E0F12] via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Assistent */}
      <section className="border-b border-white/5 px-6 py-14 md:py-16">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <SectionLabel>Ihr BA Media Assistent</SectionLabel>
            <h2 className="font-display text-3xl font-bold text-white md:text-4xl">
              In 1–2 Minuten klären, ob es passt.
            </h2>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-gray-300">
              Der Assistent fragt locker Ihre Dienstleistung, Ihre Region und Ihre aktuelle
              Auftragslage ab — und sagt Ihnen ehrlich, ob unser System für Sie sinnvoll ist.
              Ihre Anfragen gehen direkt an unser Team.
            </p>
            <p className="mt-3 text-sm text-gray-500">Kein Formular-Marathon. Einfach losschreiben.</p>
            <div className="mt-6">
              <ContactActions size="sm" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl">
              <ChatConversation variant="inline" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Preise */}
      <PricingSection onStart={openChat} />

      {/* Angebot (inkl. Risiko-Umkehr + Rückruf) */}
      <section className="px-6 py-14 md:py-16">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <div className="rounded-xl border border-[#3DDC97]/40 bg-[#14161B] p-8 md:p-10">
              <ShieldCheck className="mb-5 text-[#3DDC97]" size={36} />
              <h2 className="font-display text-2xl font-bold text-white md:text-3xl">
                Kostenlose KI-Kurzanalyse für Ihren Betrieb
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-gray-300">
                In der kostenlosen Kurzanalyse erfahren Sie konkret, wie viele Anfragen in
                Ihrer Dienstleistung und Ihrer Region realistisch sind — als klare Zahl, in ~15 Minuten.
                Unverbindlich, kein Risiko, keine Vorkasse.
              </p>
              <p className="mt-3 text-sm text-gray-500">
                Aktuell nehmen wir nur eine begrenzte Zahl neuer Betriebe pro Monat auf.
              </p>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  data-testid="angebot-cta-chat"
                  onClick={openChat}
                  className="rounded-md bg-[#3DDC97] px-7 py-4 text-base font-semibold text-[#0E0F12] transition-colors hover:bg-[#2BBF7E]"
                >
                  Jetzt kostenlos prüfen
                </button>
                <ContactActions size="sm" />
              </div>

              <div className="mt-8 border-t border-white/10 pt-6">
                <CallbackForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 py-12">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div>
            <p className="font-display text-base font-extrabold text-white">
              BA<span className="text-[#3DDC97]">.</span>Media Performance
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Neukundengewinnung für Handwerk & Bau.
            </p>
          </div>
          <div className="flex gap-6 text-sm text-gray-400">
            <Link to="/impressum" data-testid="footer-impressum" className="hover:text-[#3DDC97]">Impressum</Link>
            <Link to="/datenschutz" data-testid="footer-datenschutz" className="hover:text-[#3DDC97]">Datenschutz</Link>
            <Link to="/admin/login" className="hover:text-[#3DDC97]">Admin</Link>
          </div>
        </div>
      </footer>

      <ChatWidget />
      <AssistantLauncher />
      <StickyActions />
      <CookieBanner />
    </div>
  );
}
